import imageCompression from 'browser-image-compression';

/** Compression engages above this source size — Hani decision 2026-05-05 (ADR-012). */
export const COMPRESSION_THRESHOLD_BYTES = 5 * 1024 * 1024; // 5 MiB

/** Target size after compression — keeps Free-tier Storage friendly. */
export const COMPRESSION_TARGET_BYTES = 2 * 1024 * 1024; // 2 MiB

export type CompressOptions = {
  /** Below this, the source file is returned untouched. Defaults to 5 MiB. */
  thresholdBytes?: number;
  /** Compressor target — passes `maxSizeMB = targetBytes / (1024 * 1024)`. */
  targetBytes?: number;
};

/**
 * Pass-through if `file.size <= thresholdBytes`. Above the threshold,
 * route through `browser-image-compression` to reach `targetBytes`.
 *
 * Why a threshold rather than always-compress: the back-office uploader
 * is not a customer surface and does not need to optimise photos that
 * are already reasonably sized. Avoiding the canvas round-trip on
 * pre-optimised files preserves quality and shortens the upload UX.
 *
 * Why this lib: pure-browser, runs in a Web Worker by default
 * (`useWebWorker: true`), no Node fallback to manage. ~10 KB gzip.
 */
export async function compressIfNeeded(file: File, options: CompressOptions = {}): Promise<File> {
  const threshold = options.thresholdBytes ?? COMPRESSION_THRESHOLD_BYTES;
  const target = options.targetBytes ?? COMPRESSION_TARGET_BYTES;

  if (file.size <= threshold) {
    return file;
  }

  const compressed = await imageCompression(file, {
    maxSizeMB: target / (1024 * 1024),
    useWebWorker: true,
    initialQuality: 0.8,
  });

  return compressed;
}
